import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Info, Upload, Lock, Unlock, Settings as SettingsIcon, GitCompare, TrendingUp } from "lucide-react";
import { PayrollCompareModal } from "@/components/payroll/PayrollCompareModal";
import { SalaryDiffModal } from "@/components/payroll/SalaryDiffModal";
import { Loading, LoadError } from "@/components/LoadingState";
import { api, AYLAR } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PayrollMobileCards from "@/components/payroll/PayrollMobileCards";
import PayrollTable from "@/components/payroll/PayrollTable";
import PayrollImportModal from "@/components/payroll/PayrollImportModal";
import { PayrollPeriodFilter } from "@/components/payroll/PayrollPeriodFilter";

const QUICK_LABELS = {
  ucretli_izin_gun: "Ücretli izin",
  ucretsiz_izin_gun: "Ücretsiz izin",
  raporlu_gun: "Raporlu gün",
  ucretli_izin_manuel: "Ücretli izin",
  ucretsiz_izin_manuel: "Ücretsiz izin",
  raporlu_manuel: "Raporlu gün",
};

export default function PayrollTab({ yil, ay, onChange, refreshKey }) {
  const { canSeeMoney, canSeeKidem, canSeeTotals, isAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [lock, setLock] = useState(null);
  const [lockBusy, setLockBusy] = useState(false);
  const [lockSettingsOpen, setLockSettingsOpen] = useState(false);
  const [cutoffDay, setCutoffDay] = useState(10);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);

  const reqRef = useRef(0);
  const load = useCallback(async () => {
    const my = ++reqRef.current;
    setLoading(true);
    setLoadError(false);
    try {
      const r = await api.get(`/payroll?yil=${yil}&ay=${ay}`);
      if (my !== reqRef.current) return;
      setRows(r.data.rows);
      setLock(r.data.lock || null);
    } catch {
      if (my === reqRef.current) setLoadError(true);
      toast.error("Bordro yüklenemedi");
    } finally {
      if (my === reqRef.current) setLoading(false);
    }
  }, [yil, ay]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const updateLocal = (idx, field, value) => {
    setRows((prev) => {
      const next = [...prev];
      const flag = { yemek_gunluk_tutar: "yemek_gunluk_manuel", yemek_gun_sayisi: "yemek_gun_manuel", yol_gunluk_ucret: "yol_gunluk_manuel" }[field];
      next[idx] = { ...next[idx], [field]: value, ...(flag ? { [flag]: true } : {}) };
      return next;
    });
  };

  const buildPayload = (r) => ({
    personnel_id: r.personnel_id,
    yil, ay,
    calisma_gunu: Number(r.calisma_gunu) || 0,
    // Manuel kısmı gönder: toplam (ucretli_izin_gun) = manuel + yıllık izin.
    // Yıllık kısım annual_leave kayıtlarından her yüklemede otomatik hesaplanır.
    ucretli_izin_gun: Number(r.ucretli_izin_manuel ?? r.ucretli_izin_gun) || 0,
    ucretsiz_izin_gun: Number(r.ucretsiz_izin_manuel ?? r.ucretsiz_izin_gun) || 0,
    raporlu_gun: Number(r.raporlu_manuel ?? r.raporlu_gun) || 0,
    fazla_mesai_tutar: Number(r.fazla_mesai_tutar) || 0,
    yemek_gunluk_tutar: Number(r.yemek_gunluk_tutar) || 0,
    yemek_gun_sayisi: Number(r.yemek_gun_sayisi) || 0,
    yemek_gunluk_manuel: !!r.yemek_gunluk_manuel,
    yemek_gun_manuel: !!r.yemek_gun_manuel && r.yemek_gun_auto !== false,
    yemek_ekstra: Number(r.yemek_ekstra) || 0,
    yol_gunluk_ucret: Number(r.yol_gunluk_ucret) || 0,
    yol_gunluk_manuel: !!r.yol_gunluk_manuel,
    yemek_iade_manuel: r.yemek_iade_elle ? Number(r.yemek_iade) || 0 : null,
    yol_iade_manuel: r.yol_iade_elle ? Number(r.yol_iade) || 0 : null,
    manuel_prim: Number(r.manuel_prim) || 0,
    ek_kesinti: Number(r.ek_kesinti) || 0,
    not_metni: r.not_metni || "",
  });

  const saveRow = async (row) => {
    if (lock?.locked && !isAdmin) {
      toast.error(`${AYLAR[ay-1].n} ${yil} bordrosu kilitli. Admin onayı gerekir.`);
      return;
    }
    setSavingId(row.personnel_id);
    try {
      await api.post("/payroll", buildPayload(row));
      toast.success(`${row.ad_soyad} - bordro güncellendi`);
      load();
      onChange?.();
    } catch (e) {
      if (e?.response?.status === 423) {
        toast.error(e.response.data?.detail?.message || "Bordro kilitli");
      } else {
        toast.error("Kaydedilemedi");
      }
    } finally {
      setSavingId(null);
    }
  };

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const toggleLock = async () => {
    if (!isAdmin) return;
    setLockBusy(true);
    try {
      const url = lock?.locked ? "/payroll/unlock" : "/payroll/lock";
      const r = await api.post(url, { yil, ay });
      setLock(r.data.lock || null);
      toast.success(lock?.locked ? "Kilit açıldı" : "Ay kilitlendi");
    } catch {
      toast.error("İşlem başarısız");
    } finally {
      setLockBusy(false);
    }
  };

  const openLockSettings = async () => {
    try {
      const r = await api.get("/payroll/lock-settings");
      setCutoffDay(r.data.auto_lock_cutoff_day || 10);
    } catch { setCutoffDay(10); }
    setLockSettingsOpen(true);
  };

  const saveLockSettings = async () => {
    try {
      await api.post("/payroll/lock-settings", { auto_lock_cutoff_day: Number(cutoffDay) });
      toast.success("Otomatik kilit kuralı güncellendi");
      setLockSettingsOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kaydedilemedi");
    }
  };

  // Hızlı Puantaj: +/− butonları, debounced auto-save
  const quickTimers = useRef({});
  const quickBump = (idx, field, delta) => {
    if (lock?.locked && !isAdmin) {
      toast.error(`${AYLAR[ay-1].n} ${yil} bordrosu kilitli`);
      return;
    }
    setRows((prev) => {
      const next = [...prev];
      const current = Number(next[idx][field]) || 0;
      const value = Math.max(0, +(current + delta).toFixed(1));
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    const row = rows[idx];
    const key = `${row.personnel_id}-${field}`;
    if (quickTimers.current[key]) clearTimeout(quickTimers.current[key]);
    quickTimers.current[key] = setTimeout(() => {
      setRows((cur) => {
        const r = cur[idx];
        api.post("/payroll", buildPayload(r)).then(() => {
          toast.success(`${r.ad_soyad} · ${QUICK_LABELS[field] || field} güncellendi`, { duration: 1500 });
          load();
          onChange?.();
        }).catch(() => toast.error("Otomatik kayıt başarısız"));
        return cur;
      });
    }, 700);
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <PayrollPeriodFilter yil={yil} ay={ay} />
        <div className="bg-white border border-slate-200 rounded-xl"><Loading text="Bordro hesaplanıyor..." className="py-16" /></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <PayrollPeriodFilter yil={yil} ay={ay} />
        <div className="bg-white border border-slate-200 rounded-xl"><LoadError text="Bordro yüklenemedi." onRetry={load} className="py-16" /></div>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-10 text-center">
        <div className="text-slate-500 mb-2">Aktif personel bulunamadı</div>
        <div className="text-xs text-slate-400">Önce "Personel" sekmesinden personel ekleyin</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <PayrollPeriodFilter yil={yil} ay={ay} />
      <LockBanner
        lock={lock}
        yil={yil}
        ay={ay}
        isAdmin={isAdmin}
        lockBusy={lockBusy}
        onToggle={toggleLock}
        onOpenSettings={openLockSettings}
      />

      <div className="bg-slate-50 border border-slate-300 rounded-xl p-3 md:p-4 flex items-start gap-3">
        <Info size={16} className="text-slate-900 mt-0.5 shrink-0" />
        <div className="text-xs md:text-sm text-teal-900 flex-1">
          <strong className="font-semibold">{AYLAR[ay-1].n} {yil} Bordrosu:</strong>{" "}
          <span className="hidden md:inline">Alanlardaki değerleri doğrudan düzenleyin, net maaş otomatik hesaplanır. Değişiklikten sonra <strong>Kaydet</strong> butonuna basmayı unutmayın. Ücretsiz izin ve raporlu günler sınır olmadan tam gün kesilir.</span>
          <span className="md:hidden">Her personel kartına dokunarak değerleri düzenleyin ve <strong>Kaydet</strong>'e basın.</span>
        </div>
        {canSeeMoney && (
          <>
            <button data-testid="payroll-compare-button" onClick={() => setCompareOpen(true)}
              className="flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white px-2.5 md:px-3 py-2 rounded-lg text-xs font-semibold shrink-0">
              <GitCompare size={14} /> <span className="hidden sm:inline">Geçen Ayla</span> Karşılaştır
            </button>
            <button data-testid="salary-diff-button" onClick={() => setDiffOpen(true)}
              className="flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 md:px-3 py-2 rounded-lg text-xs font-semibold shrink-0">
              <TrendingUp size={14} /> Maaş Farkı
            </button>
          </>
        )}
        <button
          data-testid="payroll-import-button"
          onClick={() => setImportOpen(true)}
          disabled={lock?.locked && !isAdmin}
          className="flex items-center gap-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white px-2.5 md:px-3 py-2 rounded-lg text-xs font-semibold shrink-0"
        >
          <Upload size={14} /> <span className="hidden sm:inline">Toplu </span>İçe Aktar
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
        <PayrollMobileCards
          rows={rows}
          expanded={expanded}
          onToggle={toggleExpand}
          updateLocal={updateLocal}
          saveRow={saveRow}
          quickBump={quickBump}
          savingId={savingId}
          canSeeMoney={canSeeMoney}
          canSeeTotals={canSeeTotals}
          yil={yil}
          ay={ay}
          onReloadRows={load}
        />
        <PayrollTable
          rows={rows}
          expanded={expanded}
          onToggle={toggleExpand}
          updateLocal={updateLocal}
          saveRow={saveRow}
          savingId={savingId}
          canSeeMoney={canSeeMoney}
          canSeeKidem={canSeeKidem}
          canSeeTotals={canSeeTotals}
          yil={yil}
          ay={ay}
          onReloadRows={load}
        />
      </div>

      {compareOpen && <PayrollCompareModal yil={yil} ay={ay} onClose={() => setCompareOpen(false)} />}
      {diffOpen && <SalaryDiffModal yil={yil} onClose={() => setDiffOpen(false)} />}
      {importOpen && (
        <PayrollImportModal
          yil={yil}
          ay={ay}
          onClose={() => setImportOpen(false)}
          onDone={() => { setImportOpen(false); load(); onChange?.(); }}
        />
      )}

      {lockSettingsOpen && (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setLockSettingsOpen(false); }}
          data-testid="lock-settings-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
            <div>
              <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
                <SettingsIcon size={16} /> Otomatik Kilit Kuralı
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Bir sonraki ayın belirtilen gününden itibaren önceki ay otomatik kilitlenir.
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700 block mb-1">
                Kesim günü (1-28)
              </label>
              <input
                type="number"
                min="1"
                max="28"
                value={cutoffDay}
                onChange={(e) => setCutoffDay(e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:border-slate-900 focus:ring-2 focus:ring-slate-900/10 outline-none"
                data-testid="lock-cutoff-input"
              />
              <div className="mt-2 text-[11px] text-slate-500">
                Örn: 10 girilirse — bir ay sonrasının 10. gününde önceki ay otomatik kilitlenir.
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-200">
              <button
                onClick={() => setLockSettingsOpen(false)}
                className="px-3 py-1.5 border border-slate-300 text-slate-700 rounded-md text-sm font-semibold hover:bg-slate-50"
              >İptal</button>
              <button
                onClick={saveLockSettings}
                className="px-3 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-md text-sm font-semibold"
                data-testid="lock-cutoff-save"
              >Kaydet</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function LockBanner({ lock, yil, ay, isAdmin, lockBusy, onToggle, onOpenSettings }) {
  if (!lock) return null;
  const locked = lock.locked;
  const mode = lock.mode;
  const monthLabel = `${AYLAR[ay-1].n} ${yil}`;
  const bg = locked ? "bg-rose-50 border-rose-300" : "bg-emerald-50 border-emerald-300";
  const iconCls = locked ? "text-rose-700" : "text-emerald-700";
  const titleCls = locked ? "text-rose-900" : "text-emerald-900";
  const modeText = {
    manual_lock: "Admin tarafından elle kilitlendi",
    manual_unlock: "Admin tarafından açık tutuluyor",
    auto: `Otomatik kurala göre kilitli (kesim: ayın ${lock.cutoff_day}'i)`,
    open: "Açık · düzenlemeye uygun",
  }[mode] || "";
  return (
    <div
      className={`border rounded-xl p-3 md:p-4 flex items-start gap-3 ${bg}`}
      data-testid="payroll-lock-banner"
    >
      {locked ? <Lock size={18} className={`${iconCls} mt-0.5 shrink-0`} /> : <Unlock size={18} className={`${iconCls} mt-0.5 shrink-0`} />}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${titleCls}`}>
          {monthLabel} · {locked ? "KİLİTLİ" : "AÇIK"}
        </div>
        <div className="text-xs text-slate-700 mt-0.5">
          {modeText}
          {lock.by && (
            <span className="ml-1 text-slate-500">· {lock.by}</span>
          )}
        </div>
      </div>
      {isAdmin && (
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={onToggle}
            disabled={lockBusy}
            data-testid={locked ? "payroll-unlock-btn" : "payroll-lock-btn"}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md disabled:opacity-50 ${
              locked ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-rose-600 hover:bg-rose-700 text-white"
            }`}
          >
            {locked ? <><Unlock size={13} /> Kilidi Aç</> : <><Lock size={13} /> Kilitle</>}
          </button>
          <button
            onClick={onOpenSettings}
            data-testid="payroll-lock-settings-btn"
            className="inline-flex items-center gap-1 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs font-semibold px-2 py-1.5 rounded-md"
            title="Otomatik kilit ayarları"
          >
            <SettingsIcon size={13} />
          </button>
        </div>
      )}
    </div>
  );
}

